import { Alert } from '@sr/ui-library';

/**
 * Default error UI for SingleQueryResults / MultipleQueryResults.
 *
 * TanStack Query v5 types `query.error` as `TError | null` (default `TError` is
 * `Error`). Absence is `null`, not `undefined` or `unknown`:
 * - `status === 'error'` → `error: TError` (non-null)
 * - `status === 'pending' | 'success'` → `error: null`
 *
 * Call sites:
 * - SingleQueryResults passes a single `error` after `status === 'error'`
 * - MultipleQueryResults passes an array of `query.error` values; after
 *   Object.entries the discriminated narrowing is lost, so items may still be
 *   typed `TError | null`
 *
 * Prefer `Error | null | Array<Error | null>` over `unknown` — `unknown` is a
 * custom TError possibility, not Query's "no error" sentinel.
 */
export const ErrorFallback = ({
  error,
}: {
  error?: Array<Error | null> | Error | null;
}) => {
  let content: React.ReactNode;

  if (error instanceof Error) {
    content = <span>{error.message}</span>;
  } else if (Array.isArray(error) && error.some((e) => e != null)) {
    content = (
      <ul>
        {error.map(
          (e, i) =>
            e != null && (
              <li key={i}>{e instanceof Error ? e.message : String(e)}</li>
            ),
        )}
      </ul>
    );
  } else {
    content = 'An error occurred while loading data.';
  }

  return (
    <Alert title="Error" variant="error">
      {content}
    </Alert>
  );
};
